import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, BookOpen, Clock, Compass, Users } from "lucide-react";

import Header from "@/components/landing/Header";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import UtmBanner from "@/components/landing/UtmBanner";
import Footer from "@/components/landing/Footer";
import Breadcrumbs from "@/components/landing/Breadcrumbs";
import Seo from "@/components/Seo";
import { BLOG } from "@/data/blog";
import { getCornerstone } from "@/data/cornerstones";
import { resolveIcon } from "@/data/cornerstones/iconMap";

const getSupporting = (cornerstonePath, limit = 3) =>
  Object.entries(BLOG)
    .filter(([, b]) => b.cornerstone_link?.path === cornerstonePath)
    .slice(0, limit)
    .map(([slug, b]) => ({ slug, title: b.title }));

const getSupportingCount = (cornerstonePath) =>
  Object.values(BLOG).filter(
    (b) => b.cornerstone_link?.path === cornerstonePath
  ).length;

/** Most recent datePublished across all blogs in a cluster, or null. */
const getClusterLatestDate = (cornerstonePath) => {
  const dates = Object.values(BLOG)
    .filter((b) => b.cornerstone_link?.path === cornerstonePath)
    .map((b) => b.dateModified || b.datePublished)
    .filter(Boolean)
    .sort();
  return dates.length ? dates[dates.length - 1] : null;
};

/** Format YYYY-MM-DD as locale-aware "Feb 2026" / "feb 2026" / "févr. 2026". */
const formatMonthYear = (isoDate, locale) => {
  if (!isoDate) return null;
  const d = new Date(isoDate + "T00:00:00Z");
  if (Number.isNaN(d.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(locale || "nl", {
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(d);
  } catch (e) {
    return new Intl.DateTimeFormat("en", {
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(d);
  }
};

const ClusterCard = ({
  cluster,
  relatedHeading,
  readMoreCta,
  countLabel,
  countAria,
  lastUpdatedLabel,
  lastUpdatedAria,
}) => {
  const Icon = resolveIcon(cluster.icon);
  const supporting = getSupporting(cluster.path, 3);
  const total = getSupportingCount(cluster.path);
  return (
    <article
      data-testid={`cluster-${cluster.path.replace(/\//g, "")}`}
      className="flex flex-col rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6 transition-all hover:-translate-y-1 hover:border-[hsl(var(--forest))]/30"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--forest))]/10">
          <Icon className="h-5 w-5 text-[hsl(var(--forest))]" strokeWidth={1.7} />
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {total > 0 && (
            <span
              data-testid={`cluster-count-${cluster.path.replace(/\//g, "")}`}
              aria-label={countAria}
              className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--forest))]/20 bg-[hsl(var(--forest))]/5 px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--forest))]"
            >
              <BookOpen className="h-3 w-3" strokeWidth={1.8} />
              {countLabel}
            </span>
          )}
          {lastUpdatedLabel && (
            <span
              data-testid={`cluster-updated-${cluster.path.replace(/\//g, "")}`}
              aria-label={lastUpdatedAria}
              className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))] px-2.5 py-1 text-[11px] font-medium text-[hsl(var(--ink-soft))]"
            >
              <Clock className="h-3 w-3" strokeWidth={1.8} />
              {lastUpdatedLabel}
            </span>
          )}
        </div>
      </div>
      <h2 className="mt-5 font-display text-xl text-[hsl(var(--ink))]">{cluster.title}</h2>
      <p className="mt-3 text-sm text-[hsl(var(--ink-soft))]">{cluster.blurb}</p>
      <p className="mt-4 text-xs uppercase tracking-[0.16em] text-[hsl(var(--sand-dark))]">
        {cluster.audience}
      </p>

      {supporting.length > 0 && (
        <div className="mt-5 border-t border-[hsl(var(--border))] pt-4">
          <p className="mb-2 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">
            {relatedHeading}
          </p>
          <ul className="space-y-1.5 text-sm">
            {supporting.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/blog/${s.slug}`}
                  className="inline-flex items-center gap-1.5 text-[hsl(var(--ink))] hover:text-[hsl(var(--forest))]"
                >
                  <ArrowRight className="h-3 w-3" /> {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Link
        to={cluster.path}
        data-testid={`cluster-cta-${cluster.path.replace(/\//g, "")}`}
        className="mt-6 inline-flex items-center justify-center gap-1.5 rounded-full bg-[hsl(var(--forest))] px-5 py-2.5 text-sm font-medium text-[hsl(var(--background))] transition-all hover:-translate-y-0.5 hover:bg-[hsl(var(--forest-dark))]"
      >
        {readMoreCta} <ArrowRight className="h-4 w-4" />
      </Link>
    </article>
  );
};

export const Gidsen = () => {
  const { i18n } = useTranslation();
  const data = getCornerstone("gidsen", i18n.language);
  const { hub, clusters, closing, hubName } = data;

  // Dynamic total blog count across all cornerstone clusters
  const totalArticles = React.useMemo(
    () =>
      Object.values(BLOG).filter((b) =>
        clusters.some((c) => b.cornerstone_link?.path === c.path)
      ).length,
    [clusters]
  );

  const formatCount = (n) => {
    const tmpl = n === 1 ? hub.articlesCountSingular : hub.articlesCountPlural;
    return (tmpl || `${n}`).replace("{{count}}", n);
  };

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: hubName,
    itemListElement: clusters.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://equisight.nl${c.path}`,
      name: c.title,
    })),
  };

  return (
    <main data-testid="page-gidsen" className="bg-background">
      <Seo
        title={data.seo.title}
        description={data.seo.description}
        path="/gidsen"
        keywords={data.seo.keywords}
        type="article"
        breadcrumbs={[
          { name: "EquiSight", path: "/" },
          { name: hub.breadcrumbLabel, path: "/gidsen" },
        ]}
        jsonLd={itemListSchema}
      />
      <AnnouncementBar />
      <Header />
      <UtmBanner />
      <Breadcrumbs
        items={[
          { name: "EquiSight", path: "/" },
          { name: hub.breadcrumbLabel, path: "/gidsen" },
        ]}
      />

      {/* HERO */}
      <section className="mx-auto max-w-5xl px-6 py-16 lg:px-10 lg:py-24">
        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
          <BookOpen className="h-3.5 w-3.5" strokeWidth={1.8} />
          {hub.eyebrow}
        </div>
        <h1
          data-testid="page-h1"
          className="mt-4 font-display text-4xl font-medium leading-[1.05] text-[hsl(var(--ink))] sm:text-5xl lg:text-6xl"
        >
          {hub.title}
        </h1>
        <p className="mt-6 max-w-3xl text-base text-[hsl(var(--ink-soft))] sm:text-lg">
          {hub.intro}
        </p>
        <div className="mt-8 flex flex-wrap gap-2 text-xs text-[hsl(var(--ink-soft))]">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-1">
            <Users className="h-3 w-3" /> {hub.stats.guides}
          </span>
          <span
            data-testid="hub-article-total"
            className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-1"
          >
            <BookOpen className="h-3 w-3" /> {formatCount(totalArticles)}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-1">
            <Compass className="h-3 w-3" /> {hub.stats.fair}
          </span>
        </div>
      </section>

      {/* CLUSTER GRID */}
      <section className="border-t border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))]">
        <div className="mx-auto max-w-6xl px-6 py-20 lg:px-10 lg:py-24">
          <div className="grid gap-6 lg:grid-cols-2">
            {clusters.map((c) => {
              const latest = getClusterLatestDate(c.path);
              const monthYear = formatMonthYear(latest, i18n.language);
              const lastUpdatedLabel = monthYear
                ? `${hub.lastUpdatedPrefix} ${monthYear}`
                : null;
              return (
                <ClusterCard
                  key={c.path}
                  cluster={c}
                  relatedHeading={hub.relatedHeading}
                  readMoreCta={hub.readMoreCta}
                  countLabel={formatCount(getSupportingCount(c.path))}
                  countAria={hub.articlesCountAria}
                  lastUpdatedLabel={lastUpdatedLabel}
                  lastUpdatedAria={hub.lastUpdatedAria}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY THIS COLLECTION */}
      <section className="mx-auto max-w-3xl px-6 py-20 lg:px-10 lg:py-24">
        <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
          {closing.eyebrow}
        </p>
        <h2 className="mt-4 font-display text-3xl font-medium leading-[1.1] text-[hsl(var(--ink))] sm:text-4xl">
          {closing.title}
        </h2>
        <p className="mt-4 text-[hsl(var(--ink-soft))]">{closing.body1}</p>
        <p className="mt-4 text-[hsl(var(--ink-soft))]">{closing.body2}</p>
      </section>

      <Footer />
    </main>
  );
};

export default Gidsen;
